import { Box, Typography, alpha, Link, Stack, Divider } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Localized } from "@fluent/react";
import { ProcessStageOverview } from "../validationProcessApi";
import { formatDate } from "../../../../app/Utils";

interface SkippedStageAlertProps {
    stage: ProcessStageOverview;
}

export const SkippedStageAlert = ({ stage }: SkippedStageAlertProps) => {
    if (stage.status !== "SKIPPED") return null;

    const comment = stage.comment;

    const fullName = stage.lastActionByUserFullName?.trim() || "";
    const email = stage.lastActionByUserId?.trim() || ""; // u Ciebie to mail
    const when = stage.lastActionAtUtc ? new Date(stage.lastActionAtUtc) : null;

    const whenText = when ? formatDate(when, true) : "";
    const hasMeta = Boolean(fullName || email || whenText);

    return (
        <Box
            sx={(t) => ({
                mb: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${t.palette.error.light}`,
                bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.14 : 0.08),
                position: "relative",
                overflow: "hidden",
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",

                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    bgcolor: t.palette.error.main,
                },
            })}
        >
            <Box sx={{ mt: 0.2, ml: 0.5, flexShrink: 0 }}>
                <WarningAmberIcon sx={(t) => ({ color: t.palette.error.main })} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 800,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        mb: 0.25,
                    }}
                >
                    <Localized id="process-stage-skipped">_skipped</Localized>
                </Typography>

                {comment ? (
                    <Typography variant="body2" sx={{ mb: hasMeta ? 1 : 0 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            <Localized id="process-stage-skipped-reason">_reason</Localized>
                        </Box>
                        {": "}
                        {comment}
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: hasMeta ? 1 : 0 }}>
                        <Localized id="process-stage-skipped-with-no-reason">_no reason</Localized>
                    </Typography>
                )}

                {hasMeta && (
                    <>
                        <Divider sx={{ opacity: 0.6, mb: 1 }} />

                        <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonOutlineIcon fontSize="small" />

                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {fullName || "—"}
                                </Typography>

                                {email && (
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center"
                                        sx={{ ml: 0.5 }}
                                    >
                                        <EmailOutlinedIcon
                                            sx={{
                                                fontSize: 14,
                                                color: "text.secondary",
                                            }}
                                        />
                                        <Link
                                            variant="caption"
                                            href={`mailto:${email}`}
                                            underline="hover"
                                            sx={{
                                                fontWeight: 500,
                                                color: "text.secondary",
                                            }}
                                        >
                                            ({email})
                                        </Link>
                                    </Stack>
                                )}
                            </Stack>


                            <Stack direction="row" spacing={1} alignItems="center">
                                <ScheduleIcon fontSize="small" />

                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {whenText || "—"}
                                </Typography>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Box>
        </Box>
    );
};
